import {ChangeDetectionStrategy, Component, OnDestroy, OnInit} from '@angular/core'
import {LinkService} from '../../../../core/i18n/link.service'
import {PUBLIC_ROUTES} from '../../../../shared/routing/public.routes'
import {AbstractControl, UntypedFormBuilder, UntypedFormControl, Validators} from '@angular/forms'
import {FormService} from '../../../../core/forms/form.service'
import {ActivatedRoute, Router} from '@angular/router'
import {Salutation} from '../../../../../__generated__/types'
import {Observable, Subject} from 'rxjs'
import {JobRequestRepository} from '../job-request.repository'
import {FooterProgressSteps} from '../job-request-footer/job-request-footer.component'
import {map, startWith, takeUntil} from 'rxjs/operators'
import {AgencyResolved} from '../resolvers/job-request-agency.resolver'
import {conditionalValidator, isControlInvalid} from '../../../../craft/forms/form.types'
import {PageComponent, PageMetadata} from '../../../../core/seo/page-metadata'
import {JobRequestPageMetadataService} from '../job-request-page-metadata.service'
import {OfferingResolved} from '../resolvers/job-request-offering.resolver'
import {faCopy} from '@fortawesome/pro-solid-svg-icons/faCopy'
import {ClientLastJob, JobRequestService} from '../job-request.service'

@Component({
  templateUrl: './job-request-contact-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {ngSkipHydration: 'true'},
  standalone: false,
})
export class JobRequestContactPageComponent extends PageComponent implements OnInit, OnDestroy {
  private readonly nextPath = PUBLIC_ROUTES.JOB_REQUEST_SUMMARY
  private readonly previousPath = PUBLIC_ROUTES.JOB_REQUEST_EXECUTION_TIME
  readonly salutations = [
    {key: Salutation.Madam, valueTranslationKey: 'salutationList.MADAM'},
    {key: Salutation.Sir, valueTranslationKey: 'salutationList.SIR'},
    {key: Salutation.None, valueTranslationKey: 'salutationList.NONE'},
  ]
  private readonly unsubscribe$ = new Subject<void>()
  readonly agency?: AgencyResolved = this.route.snapshot.data['agency']
  readonly offering: OfferingResolved = this.route.snapshot.data['offering'] ?? {
    label: this.jobRequestRepository.readJobRequestDetailState().title,
    executionAddressEnabled: true,
  }
  readonly clientLastJob: ClientLastJob | undefined = this.route.snapshot.data['clientLastJob']

  readonly billingAddressNotIdenticalControl = new UntypedFormControl(false, [])
  readonly billingAddressControls = {
    streetAndHouseNumber: new UntypedFormControl(
      '',
      conditionalValidator(() => this.billingAddressNotIdenticalControl.value, [Validators.required]),
    ),
    postalCode: new UntypedFormControl(
      '',
      conditionalValidator(
        () => this.billingAddressNotIdenticalControl.value,
        [Validators.required, Validators.maxLength(4), Validators.minLength(4)],
      ),
    ),
    locality: new UntypedFormControl(
      '',
      conditionalValidator(() => this.billingAddressNotIdenticalControl.value, [Validators.required]),
    ),
  }

  readonly billingClientNotIdenticalControl = new UntypedFormControl(false, [])
  readonly billingClientControls = {
    salutation: new UntypedFormControl(
      undefined,
      conditionalValidator(() => this.billingAddressNotIdenticalControl.value, [Validators.required]),
    ),
    firstName: new UntypedFormControl(
      '',
      conditionalValidator(() => this.billingAddressNotIdenticalControl.value, [Validators.required]),
    ),
    lastName: new UntypedFormControl(
      '',
      conditionalValidator(() => this.billingAddressNotIdenticalControl.value, [Validators.required]),
    ),
    company: new UntypedFormControl(
      '',
      conditionalValidator(() => this.billingAddressNotIdenticalControl.value, []),
    ),
    phoneNumber: new UntypedFormControl(
      '',
      conditionalValidator(() => this.billingAddressNotIdenticalControl.value, [Validators.required]),
    ),
    email: new UntypedFormControl(
      '',
      conditionalValidator(() => this.billingAddressNotIdenticalControl.value, [Validators.required, Validators.email]),
    ),
  }

  readonly contactControls = {
    salutation: new UntypedFormControl(undefined, [Validators.required]),
    firstName: new UntypedFormControl('', [Validators.required]),
    lastName: new UntypedFormControl('', [Validators.required]),
    company: new UntypedFormControl('', []),
    phoneNumber: new UntypedFormControl('', [Validators.required]),
    email: new UntypedFormControl('', [Validators.required, Validators.email]),
  }
  readonly contactControlsFormGroup = this.fb.group(this.contactControls)
  readonly billingAddressControlsFormGroup = this.fb.group(this.billingAddressControls)
  readonly billingClientControlsFormGroup = this.fb.group(this.billingClientControls)

  readonly billingDetailsControls = this.fb.group({
    billingAddressNotIdentical: this.billingAddressNotIdenticalControl,
    billingClientNotIdentical: this.billingClientNotIdenticalControl,
    billingClient: this.billingClientControlsFormGroup,
    billingAddress: this.billingAddressControlsFormGroup,
  })

  readonly form = this.fb.group({
    contactControls: this.contactControlsFormGroup,
    billingDetailsControls: this.billingDetailsControls,
  })

  readonly progressSteps: FooterProgressSteps = {
    currentStep$: this.form.valueChanges.pipe(
      startWith(this.form),
      map(() => {
        return this.controlsForProgressCalculation.filter(c => c.valid).length
      }),
    ),
    numberOfSteps: this.controlsForProgressCalculation.length,
  }

  isDamageRequest = false

  constructor(
    readonly jobRequestService: JobRequestService,
    readonly linkService: LinkService,
    readonly formService: FormService,
    private readonly fb: UntypedFormBuilder,
    private readonly jobRequestRepository: JobRequestRepository,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly jobRequestPageMetadataService: JobRequestPageMetadataService,
  ) {
    super()
  }

  getPageMetadata(): Observable<PageMetadata> {
    return this.jobRequestPageMetadataService.getPageMetadataForStep('CONTACT', this.offering)
  }

  private get controlsForProgressCalculation(): AbstractControl[] {
    const controls: AbstractControl[] = Object.values(this.form.controls)
    return controls.filter(
      value => value !== this.contactControls.company && value !== this.billingClientControls.company,
    )
  }

  private observeBillingAddressNotIdentical() {
    // updates conditional validators on billingAddress FormGroup
    this.billingAddressNotIdenticalControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(value => {
      Object.values({...this.billingAddressControls, ...this.billingClientControls}).forEach(
        (control: UntypedFormControl) => {
          control.updateValueAndValidity()
        },
      )
    })
  }

  private observeBillingClientNotIdentical() {
    // updates conditional validators on billingClient FormGroup
    this.billingClientNotIdenticalControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(value => {
      Object.values(this.billingClientControls).forEach((control: UntypedFormControl) => {
        control.updateValueAndValidity()
      })
    })
  }

  ngOnInit(): void {
    this.hydrateForm()
    this.observeBillingAddressNotIdentical()
    this.observeBillingClientNotIdentical()
  }

  hydrateForm() {
    const contactState = this.jobRequestRepository.readJobRequestContactState()
    if (contactState) {
      this.form.patchValue({contactControls: contactState})
    } else if (this.clientLastJob) {
      // if there's nothing in state, auto-fill with the last job of the client
      this.form.patchValue({contactControls: this.clientLastJob.client})
    }

    // billing Address details is required when the execution address is disabled.
    const billingAddressRequired = !this.offering?.executionAddressEnabled
    if (billingAddressRequired) {
      this.billingAddressNotIdenticalControl.setValue(true)
    }
    const billingDetailsState = this.jobRequestRepository.readJobRequestBillingDetailsState()
    if (billingDetailsState) {
      this.form.patchValue({billingDetailsControls: billingDetailsState})
    } else if (this.clientLastJob && billingAddressRequired) {
      // if there's nothing in state, auto-fill with the last job of the client
      this.billingAddressControls.locality.patchValue(this.clientLastJob.billingAddress.locality)
      this.billingAddressControls.postalCode.patchValue(this.clientLastJob.billingAddress.postalCode)
      this.billingAddressControls.streetAndHouseNumber.patchValue(
        this.clientLastJob.billingAddress.streetAndHouseNumber,
      )
    }

    const damage = this.jobRequestRepository.readJobRequestDamageState()
    this.isDamageRequest = !!damage
  }

  next(): void {
    this.form.updateValueAndValidity()
    this.form.markAllAsTouched()

    if (!this.offering?.executionAddressEnabled && !this.billingClientNotIdenticalControl.value) {
      this.billingClientControlsFormGroup.setValue(this.contactControlsFormGroup.value)
    }

    if (this.form.valid) {
      // getRawValue will also take the disabled field values (postal code and locality)
      this.jobRequestRepository.storeJobRequestContactState(this.contactControlsFormGroup.getRawValue())
      this.jobRequestRepository.storeJobRequestBillingDetails(this.billingDetailsControls.getRawValue())
      this.router.navigate([this.nextPath.routingPath], {
        relativeTo: this.route.parent,
        queryParamsHandling: 'merge',
      })
    }
  }

  previous(): void {
    this.jobRequestRepository.storeJobRequestContactState(this.contactControlsFormGroup.value)
    this.jobRequestRepository.storeJobRequestBillingDetails(this.billingDetailsControls.value)
    this.router.navigate([this.previousPath.routingPath], {
      relativeTo: this.route.parent,
      queryParamsHandling: 'merge',
    })
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  protected readonly isControlInvalid = isControlInvalid
  protected readonly copyIcon = faCopy
}
